/**
 * @file schemas/audit-doc-review.ts
 * @description 稽核員開案文件審查（客戶 PPT S38）前後端共用 Zod Schema。
 *
 * 業務規則：
 * - 稽核任務「開案準備」＝文件審查：稽核員建一輪審查（自動建【文件審查紀錄】表單，
 *   TOP＝D-ZHSR02-27）→ 填表後判定：'pass' 通過（該輪關閉）；'supplement' 需補件 →
 *   自動建【文件審查通知單】（TOP＝D-ZHSR02-28）並通知管理組轉達客戶。
 * - 收到補件後上傳關聯（fileIds 合併去重），可再建新一輪審查直至通過。
 *
 * ⚠️ 與受理階段 case-document-reviews（admin/manager 申請文件審核 gate）是兩個不同物件。
 *
 * 獨立成檔原因：schemas/m2.ts 為其他會話在途檔案，避免衝突。
 */
import { z } from 'zod';
/** 審查結果枚舉：pass 通過 / supplement 需補件 */
export const AUDIT_DOC_REVIEW_RESULTS = ['pass', 'supplement'];
/** 審查結果中文標籤（UI 顯示用） */
export const AUDIT_DOC_REVIEW_RESULT_LABELS = {
    pass: '通過',
    supplement: '需補件',
};
/**
 * 判定一輪文件審查的請求體（POST /api/audit-tasks/:taskId/doc-reviews/:id/decide）。
 * - result：'pass' 該輪關閉；'supplement' 建通知單並通知管理組
 * - resultNote：判定備註（選填，補件時建議填寫缺漏說明）
 */
export const decideAuditDocReviewSchema = z.object({
    result: z.enum(AUDIT_DOC_REVIEW_RESULTS),
    resultNote: z.string().trim().max(1000, '備註最多 1000 字').optional(),
});
/**
 * 上傳客戶補件的請求體（POST /api/audit-tasks/:taskId/doc-reviews/:id/supplement）。
 * fileIds：files 表 ID 陣列（至少 1 個；服務端與既有清單合併去重）。
 *
 * ⚠️（序66）此【補件】＝稽核員文件審查時「證據不足 / 不符」的客戶補件，
 * 與管理組派案文件點檢的【補充】（應附文件不齊 / 不清，序57）是兩個不同概念，
 * 前端 UI 文案以「稽核文件審查補件」明確標示區分。
 */
export const supplementAuditDocReviewSchema = z.object({
    fileIds: z.array(z.number().int().positive()).min(1, '至少需上傳 1 個補件檔案').max(50),
});
/**
 * 單輪文件審查 DTO（GET /api/audit-tasks/:taskId/doc-reviews 列表項）。
 */
export const auditDocReviewItemSchema = z.object({
    /** 記錄 ID */
    id: z.number().int(),
    /** 關聯稽核任務 ID */
    auditTaskId: z.number().int(),
    /** 【文件審查紀錄】表單實例 ID（點擊進表單填寫頁） */
    reviewFormInstanceId: z.number().int(),
    /** 【文件審查通知單】表單實例 ID（supplement 判定後才有；否則 null） */
    noticeFormInstanceId: z.number().int().nullable(),
    /** 審查結果（null = 審查中，尚未判定） */
    result: z.enum(AUDIT_DOC_REVIEW_RESULTS).nullable(),
    /** 判定備註 */
    resultNote: z.string().nullable(),
    /** 補件通知時間（ISO 字串；supplement 判定時寫入） */
    notifiedAt: z.string().nullable(),
    /** 客戶補件檔案 ID 陣列（無補件時為空陣列） */
    supplementFileIds: z.array(z.number().int()),
    /** 該輪關閉時間（ISO 字串；pass 判定時寫入） */
    closedAt: z.string().nullable(),
    /** 建立者 ID（任務 assignee 稽核員） */
    createdBy: z.number().int().nullable(),
    /** 建立者姓名（join users；用戶已刪時為 null） */
    createdByName: z.string().nullable(),
    /** 建立時間（ISO 字串） */
    createdAt: z.string(),
    /** 更新時間（ISO 字串） */
    updatedAt: z.string(),
});
/**
 * （序65）文件審查介面「要比對的案件附件」摘要。
 *
 * 開案文件審查需與【案件附件】關聯，供稽核員逐項比對（可下載 / 預覽）。
 * 來源＝ case_documents（受理階段客戶上傳的申請文件），JOIN files 帶出檔名 / 大小。
 */
export const auditDocReviewCaseDocumentSchema = z.object({
    /** case_documents 記錄 ID */
    id: z.number().int(),
    /** 文件類型標籤（申請書 / 身分證 / 土地所有權狀 …） */
    docType: z.string(),
    /** 關聯 files 表 ID（下載 / 預覽走 /api/files 預簽名） */
    fileId: z.number().int(),
    /** 原始檔名（files.filename；缺失時為 null） */
    filename: z.string().nullable(),
    /** 檔案大小 bytes（files.sizeBytes；缺失時為 null） */
    sizeBytes: z.number().int().nullable(),
    /** 文件備註 */
    note: z.string().nullable(),
});
